import {
  BackEndValueType,
  CommonHelperFunctions,
  ECardinality,
  ECardinalityConversion,
  ESymbolCategory,
  ISymbol
} from "@kuika/kuika-cl-model";
import type { IAction, IKType, IKTypeField, IProperty, IScreenDesignDictionary } from "@kuika/kuika-cl-model";
import type { IActionDefinitionDto } from "@kuika/kuika-cl-model/src/dtos/action.dto";
import { PropertyHelper } from "../property-helper";
import { ScreenDesignHelper } from "../screen-design-helper";

/**
 * Upgrades a flat `category=actionField` symbol (from a
 * `actionField:<id>:<field>` symbolRef) into the `category=fieldOfType`
 * shape the human designer's picker emits — filling in the action-
 * definition reference, the cardinality conversion when a list-returning
 * action is bound to a scalar target, and the type conversion when the
 * field type doesn't match the target type.
 *
 * Without enrichment, LLM-driven bindings render as
 * `actionField:[<guid>].Field` and fail at runtime when any converter
 * is needed. With it, the same ref becomes
 * `fieldOfType:[<ActionName>].first.Field.toString`.
 */

export interface EnrichActionFieldContext {
  // Pass-through when undefined — converters require the return-type model.
  actionDefinition: IActionDefinitionDto | undefined;
  // Pass-through when undefined — type converter selection requires it.
  targetType: string | undefined;
  // Defaults to ECardinality.One (scalar targets are the common case).
  targetCardinality?: ECardinality;
}

export function enrichActionFieldSymbol(baseSymbol: ISymbol, context: EnrichActionFieldContext): ISymbol {
  if (baseSymbol.category !== ESymbolCategory.actionField) return baseSymbol;

  const { actionDefinition, targetType } = context;
  if (!actionDefinition || !targetType) return baseSymbol;

  const fieldPath = baseSymbol.fieldName ?? "";
  if (fieldPath === "") return baseSymbol;

  const field = findFieldOnType(actionDefinition.returnTypeModel, fieldPath);
  if (!field) return baseSymbol;

  const fieldType = field.type?.name ?? "Unknown";
  const targetCardinality = context.targetCardinality ?? ECardinality.One;
  const needsCardinalityConversion =
    actionDefinition.returnCardinality === ECardinality.Many && targetCardinality === ECardinality.One;
  const converterOfType = ScreenDesignHelper.generateTypeConversion(fieldType, targetType)[0]?.typeConversion as
    | string
    | undefined;

  const parts: string[] = [`[${actionDefinition.name}]`];
  if (needsCardinalityConversion) parts.push(".first");
  parts.push(`.${fieldPath}`);
  if (converterOfType) parts.push(`.${converterOfType}`);

  const enriched: ISymbol = {
    ...baseSymbol,
    category: ESymbolCategory.fieldOfType,
    label: "Action Result",
    description: parts.join(""),
    actionDefinitionId: actionDefinition.id,
    fieldName: fieldPath,
    currentType: fieldType as BackEndValueType,
    targetType: fieldType as BackEndValueType
  };

  if (needsCardinalityConversion) {
    enriched.converterOfCardinality = ECardinalityConversion.first;
    enriched.cardinality = ECardinality.One;
  }
  if (converterOfType) {
    enriched.converterOfType = converterOfType;
  }

  return enriched;
}

// Walks screen events for an action instance whose `id === resultActionId`
// and resolves its `actionDefinitionId` to a catalog entry. Recurses into
// nested actions (If/Else, ForEach, Recurring, MqttSubscribe) via
// CommonHelperFunctions.getChildActionFromAction — a shallow walk would
// miss actions defined inside control-flow wrappers.
export function findActionDefinitionByResultActionId(
  screenDesignDictionary: IScreenDesignDictionary,
  actionDefinitions: readonly IActionDefinitionDto[],
  resultActionId: string
): IActionDefinitionDto | undefined {
  const match = findActionInstance(screenDesignDictionary, resultActionId);
  if (!match?.actionDefinitionId) return undefined;
  return actionDefinitions.find((d) => d.id === match.actionDefinitionId);
}

function findActionInstance(
  screenDesignDictionary: IScreenDesignDictionary,
  resultActionId: string
): IAction | undefined {
  for (const di of Object.values(screenDesignDictionary)) {
    const events = di?.item?.events;
    if (!events || events.length === 0) continue;
    for (const event of events) {
      const actions = event?.actions;
      if (!actions || actions.length === 0) continue;
      for (const topAction of actions) {
        const flat = CommonHelperFunctions.getChildActionFromAction(topAction);
        const hit = flat.find((a) => a?.id === resultActionId);
        if (hit) return hit;
      }
    }
  }
  return undefined;
}

export interface TargetPropertyShape {
  targetType: string;
  targetCardinality: ECardinality;
}

// Reuses PropertyHelper.calculateBackEndValueType so the same inference the
// right-panel picker uses (NumberInput → Int64, Switch → Boolean,
// DatePicker → DateTimeOffset, …) fires for the LLM path too.
export function findTargetPropertyShape(
  screenDesignDictionary: IScreenDesignDictionary,
  kuikaId: string,
  propertyName: string
): TargetPropertyShape | undefined {
  const di = screenDesignDictionary[Number(kuikaId)];
  const property = di?.item?.properties?.find((p) => p.name === propertyName) as IProperty | undefined;
  if (!property) return undefined;
  const targetType = new PropertyHelper().calculateBackEndValueType(property);
  const targetCardinality = property.isList === true ? ECardinality.Many : ECardinality.One;
  return { targetType, targetCardinality };
}

function findFieldOnType(type: IKType | undefined, fieldPath: string): IKTypeField | undefined {
  if (!type?.elements || type.elements.length === 0) return undefined;
  // Dotted paths (e.g. `customer.name`) would need recursive descent into
  // the nested typeModel. Fall through on a miss rather than fabricating one.
  return type.elements.find((el) => el.name === fieldPath);
}
